import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  Banknote,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  DollarSign,
  CreditCard,
  Smartphone,
  HandCoins,
  ShieldCheck,
  ShieldAlert,
  Timer,
  FileCheck,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface RepaymentData {
  summary: {
    dueThisMonth: number;
    paidThisMonth: number;
    unpaidThisMonth: number;
    overdueInstallments: number;
    salaryDeductionRepayments: number;
    manualRepayments: number;
    pendingVerification: number;
  };
  paymentMethodBreakdown: Array<{
    method: string;
    amount: number;
    count: number;
  }>;
  verificationBreakdown: Array<{
    status: string;
    count: number;
  }>;
  repayments: Array<{
    loanId: string;
    memberName: string;
    installment: number;
    dueDate: string;
    amount: number;
    paid: number;
    status: string;
    method: string;
    verified: boolean;
  }>;
}

const PIE_COLORS = ["#009A44", "#00C853", "#4CAF50", "#81C784", "#A5D6A7", "#C8E6C9"];

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
    gray: "bg-gray-100",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const statusBadge: Record<string, string> = {
  paid: "bg-[#009A44]/10 text-[#009A44]",
  partial: "bg-amber-50 text-amber-600",
  unpaid: "bg-red-50 text-red-600",
  overdue: "bg-red-50 text-red-600",
  pending: "bg-blue-50 text-blue-600",
};

export default function RepaymentReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-repayments", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.paymentMethod && filters.paymentMethod !== "all") params.paymentMethod = filters.paymentMethod;

      const { data } = await api.get("/reports/repayments", { params });
      return data?.data as RepaymentData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || {
    dueThisMonth: 0,
    paidThisMonth: 0,
    unpaidThisMonth: 0,
    overdueInstallments: 0,
    salaryDeductionRepayments: 0,
    manualRepayments: 0,
    pendingVerification: 0,
  };
  const paymentMethodBreakdown = rawData?.paymentMethodBreakdown || [];
  const verificationBreakdown = rawData?.verificationBreakdown || [];
  const repayments = rawData?.repayments || [];

  const exportColumns = [
    { key: "loanId", label: "Loan ID" },
    { key: "memberName", label: "Member" },
    { key: "installment", label: "Installment" },
    { key: "dueDate", label: "Due Date" },
    { key: "amount", label: "Amount" },
    { key: "paid", label: "Paid" },
    { key: "status", label: "Status" },
    { key: "method", label: "Method" },
    { key: "verified", label: "Verified" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Repayment Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Loan repayment tracking and verification status
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load repayment report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <Banknote className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Repayment Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Loan repayment tracking and verification status
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year", "status", "paymentMethod"]}
        />

        {/* Summary Cards - Row 1 */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Due This Month"
            value={summary.dueThisMonth}
            icon={<Timer className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
          <SummaryCard
            title="Paid This Month"
            value={summary.paidThisMonth}
            icon={<CheckCircle2 className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Unpaid This Month"
            value={summary.unpaidThisMonth}
            icon={<XCircle className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Overdue Installments"
            value={summary.overdueInstallments}
            icon={<AlertTriangle className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
        </div>

        {/* Summary Cards - Row 2 */}
        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard
            title="Salary Deduction Repayments"
            value={summary.salaryDeductionRepayments}
            icon={<CreditCard className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Manual Repayments"
            value={summary.manualRepayments}
            icon={<HandCoins className="h-5 w-5" />}
            accentColor="purple"
            loading={loading}
          />
          <SummaryCard
            title="Pending Verification"
            value={summary.pendingVerification}
            icon={<ShieldAlert className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Payment Method Breakdown Pie Chart */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <Banknote className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Payment Method Breakdown</h3>
              </div>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : paymentMethodBreakdown.length === 0 ? (
                <div className="py-12 text-center">
                  <Banknote className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  <p className="text-sm text-gray-500">No payment method data available</p>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentMethodBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={4}
                        dataKey="amount"
                        nameKey="method"
                      >
                        {paymentMethodBreakdown.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Verification Status Bar Chart */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Verification Status</h3>
              </div>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : verificationBreakdown.length === 0 ? (
                <div className="py-12 text-center">
                  <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                  <p className="text-sm text-gray-500">No verification data available</p>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={verificationBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar dataKey="count" name="Count" fill="#009A44" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Repayment Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <DollarSign className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Repayment Details</h3>
              </div>
              <ReportExport
                data={repayments}
                filename="repayment-report"
                title="Repayment Report"
                columns={exportColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : repayments.length === 0 ? (
              <div className="py-12 text-center">
                <Banknote className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No repayment data found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Loan ID</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Member</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Installment</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Due Date</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Amount</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Paid</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Status</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Method</th>
                      <th className="pb-2 font-medium text-gray-500">Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repayments.map((repayment, index) => (
                      <tr
                        key={`${repayment.loanId}-${repayment.installment}-${index}`}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-mono text-xs text-gray-500">
                          {repayment.loanId}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {repayment.memberName}
                        </td>
                        <td className="py-3 pr-4 text-right text-gray-700">
                          {repayment.installment}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {repayment.dueDate ? new Date(repayment.dueDate).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                          {formatCurrency(repayment.amount)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-[#009A44]">
                          {formatCurrency(repayment.paid)}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              statusBadge[repayment.status] || "bg-gray-100 text-gray-600"
                            )}
                          >
                            {repayment.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-600 capitalize">
                          {repayment.method.replace("_", " ")}
                        </td>
                        <td className="py-3">
                          {repayment.verified ? (
                            <CheckCircle2 className="h-4 w-4 text-[#009A44]" />
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-300" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
